import { dateFromDateString, getDateString, getNormalizedDate, jsonDateReviver } from './dateUtils';
import {
  craftGameStorageKey,
  craftHistoryStorageKey,
  GAME_CONFIG_KEY,
  GameConfig,
  GameHistory,
  GameMode,
  GameState,
  GameStatus
} from './gameState';

const DEPRECATED_GAME_MODE_KEY_V2 = 'activeGameMode';
const DEPRECATED_GAME_MODE_KEY = 'fourdleGameMode';
const DEPRECATED_DAILY_COMPLETED_AT_KEY = 'dailyCompletedDate';
const DEPRECATED_ENCORE_COMPLETED_AT_KEY = 'encoreCompletedDate';
const DEPRECATED_DAILY_GAME_KEY_V2 = 'gameState:daily';
const DEPRECATED_DAILY_GAME_KEY = 'fourdleGameStateDaily';
const DEPRECATED_ENCORE_GAME_KEY = 'fourdleGameStateEncore';
const DEPRECATED_ENCORE_HINT_LETTER_KEY = 'encoreHintLetter';
const DEPRECATED_ENCORE_HINT_LETTER_DATE_KEY = 'encoreHintLetterDate';
const DEPRECATED_HAS_PLAYED_KEY = 'loadHasPlayedBefore';

export interface StorageWriteOperation {
  key: string;
  value: unknown;
}

export interface StorageMigrationInput {
  games: Record<string, GameState>;
  histories: Record<GameMode, GameHistory>;
  config: GameConfig;
}

export interface StorageMigrationOutput {
  games: Record<string, GameState>;
  histories: Record<GameMode, GameHistory>;
  config: GameConfig;
  writes: StorageWriteOperation[];
}

type StorageMigration = (input: StorageMigrationInput) => StorageMigrationOutput;

// Restricts inferred completion states to terminal outcomes so migration never writes IN_PROGRESS.
// This keeps migrated completion records compatible with eligibility checks.
const isTerminalGameStatus = (status: unknown): status is GameStatus.WIN | GameStatus.LOSE => {
  return status === GameStatus.WIN || status === GameStatus.LOSE;
};

// Guards date strings read from legacy localStorage keys before using them in date parsing.
// This prevents bad legacy values from creating invalid completion records.
const isValidDateString = (value: unknown): value is string => {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
};

// Looks up a game from the already loaded in-memory map first, then falls back to localStorage.
// This allows migrations to inspect legacy keys that are intentionally excluded from normal loading.
const getGameByStorageKey = (key: string, games: Record<string, GameState>): GameState | null => {
  if (games[key]) return games[key];

  const raw = localStorage.getItem(key);
  if (!raw) return null;

  try {
    return JSON.parse(raw, jsonDateReviver) as GameState;
  } catch {
    return null;
  }
};

// Infers a migrated completion status for a legacy completedDate.
// Legacy stores often only kept dates, so we probe likely game-state keys and default to WIN
// when no terminal state can be recovered (sufficient for "completed today" gating).
const inferCompletionStatus = (
  mode: GameMode.DAILY | GameMode.ENCORE,
  completedDate: string,
  games: Record<string, GameState>
): GameStatus => {
  const completedDateObj = dateFromDateString(completedDate);
  const candidates = mode === GameMode.DAILY
    ? [craftGameStorageKey(GameMode.DAILY, completedDateObj), DEPRECATED_DAILY_GAME_KEY_V2, DEPRECATED_DAILY_GAME_KEY]
    : [craftGameStorageKey(GameMode.ENCORE), DEPRECATED_ENCORE_GAME_KEY];

  for (const key of candidates) {
    const game = getGameByStorageKey(key, games);
    if (game && isTerminalGameStatus(game.gameStatus)) {
      return game.gameStatus;
    }
  }

  // Legacy stores only completion date, not status; WIN is a safe default for eligibility.
  return GameStatus.WIN;
};

// Migrates old mode-only persistence into the modern gameConfig object.
// We do this so all runtime config reads can rely on a single canonical key.
const migrateLegacyModeToGameConfig: StorageMigration = ({
  games,
  histories,
  config
}: StorageMigrationInput): StorageMigrationOutput => {
  if (localStorage.getItem(GAME_CONFIG_KEY)) {
    return { games, histories, config, writes: [] };
  }

  const legacyMode = localStorage.getItem(DEPRECATED_GAME_MODE_KEY_V2) || localStorage.getItem(DEPRECATED_GAME_MODE_KEY);
  if (!legacyMode) {
    return { games, histories, config, writes: [] };
  }

  const migratedConfig: GameConfig = legacyMode === GameMode.RANDOM
    ? { mode: GameMode.RANDOM, date: null }
    : { mode: GameMode.DAILY, date: getNormalizedDate() };

  const storableConfig = {
    mode: migratedConfig.mode,
    date: migratedConfig.date ? getDateString(migratedConfig.date) : null
  };

  return {
    games,
    histories,
    config: migratedConfig,
    writes: [{ key: GAME_CONFIG_KEY, value: storableConfig }]
  };
};

// Backfills completions maps from legacy completion-date storage for Daily/Encore.
// Idempotent by design: if completions already exist, migration skips that history.
const backfillCompletionsFromLegacyDates: StorageMigration = ({
  games,
  histories,
  config
}: StorageMigrationInput): StorageMigrationOutput => {
  const migratedHistories: Record<GameMode, GameHistory> = {
    ...histories
  };
  const writes: StorageWriteOperation[] = [];
  const modesToBackfill: Array<GameMode.DAILY | GameMode.ENCORE> = [GameMode.DAILY, GameMode.ENCORE];

  for (const mode of modesToBackfill) {
    const history = migratedHistories[mode] || {};
    const hasCompletions = Boolean(history.completions && Object.keys(history.completions).length > 0);
    if (hasCompletions) continue;

    const deprecatedKey = mode === GameMode.DAILY
      ? DEPRECATED_DAILY_COMPLETED_AT_KEY
      : DEPRECATED_ENCORE_COMPLETED_AT_KEY;
    const completedDate = history.lastCompletedDate || localStorage.getItem(deprecatedKey);
    if (!isValidDateString(completedDate)) continue;

    const completionStatus = inferCompletionStatus(mode, completedDate, games);
    const migratedHistory: GameHistory = {
      ...history,
      lastCompletedDate: completedDate,
      completions: {
        [completedDate]: completionStatus
      }
    };

    migratedHistories[mode] = migratedHistory;
    writes.push({
      key: craftHistoryStorageKey(mode),
      value: migratedHistory
    });
  }

  return {
    games,
    histories: migratedHistories,
    config,
    writes
  };
};

// Migrates older hint and first-time-play flags into current gameHistory records.
// This lets the rest of the app read only modern history keys without legacy fallbacks.
const migrateLegacyHintAndPlayFlags: StorageMigration = ({
  games,
  histories,
  config
}: StorageMigrationInput): StorageMigrationOutput => {
  const migratedHistories: Record<GameMode, GameHistory> = {
    ...histories
  };
  const writes: StorageWriteOperation[] = [];

  const dailyHistory = migratedHistories[GameMode.DAILY] || {};
  const migratedDailyHistory: GameHistory = { ...dailyHistory };
  const legacyHint = localStorage.getItem(DEPRECATED_ENCORE_HINT_LETTER_KEY);
  const legacyHintDate = localStorage.getItem(DEPRECATED_ENCORE_HINT_LETTER_DATE_KEY);

  if (!migratedDailyHistory.hintLetter && legacyHint && legacyHint.length === 1) {
    migratedDailyHistory.hintLetter = legacyHint.toLowerCase();
  }

  if (!migratedDailyHistory.hintLetterDate && isValidDateString(legacyHintDate)) {
    migratedDailyHistory.hintLetterDate = legacyHintDate;
  }

  if (
    migratedDailyHistory.hintLetter !== dailyHistory.hintLetter ||
    migratedDailyHistory.hintLetterDate !== dailyHistory.hintLetterDate
  ) {
    migratedHistories[GameMode.DAILY] = migratedDailyHistory;
    writes.push({
      key: craftHistoryStorageKey(GameMode.DAILY),
      value: migratedDailyHistory
    });
  }

  const randomHistory = migratedHistories[GameMode.RANDOM] || {};
  const legacyHasPlayed = localStorage.getItem(DEPRECATED_HAS_PLAYED_KEY);
  if (
    randomHistory.hasPlayedBefore === undefined &&
    (legacyHasPlayed === 'true' || legacyHasPlayed === 'false')
  ) {
    const migratedRandomHistory: GameHistory = {
      ...randomHistory,
      hasPlayedBefore: legacyHasPlayed === 'true'
    };

    migratedHistories[GameMode.RANDOM] = migratedRandomHistory;
    writes.push({
      key: craftHistoryStorageKey(GameMode.RANDOM),
      value: migratedRandomHistory
    });
  }

  return {
    games,
    histories: migratedHistories,
    config,
    writes
  };
};

const STORAGE_MIGRATIONS: StorageMigration[] = [
  migrateLegacyModeToGameConfig,
  backfillCompletionsFromLegacyDates,
  migrateLegacyHintAndPlayFlags
];

// Runs migrations in order and accumulates write operations to persist after transformation.
// Keeping writes side-loaded avoids in-place mutation and keeps each migration predictable.
export const applyStorageMigrations = (input: StorageMigrationInput): StorageMigrationOutput => {
  return STORAGE_MIGRATIONS.reduce<StorageMigrationOutput>(
    (accumulator, migrate) => {
      const migrated = migrate({
        games: accumulator.games,
        histories: accumulator.histories,
        config: accumulator.config
      });

      return {
        games: migrated.games,
        histories: migrated.histories,
        config: migrated.config,
        writes: [...accumulator.writes, ...migrated.writes]
      };
    },
    {
      games: input.games,
      histories: input.histories,
      config: input.config,
      writes: []
    }
  );
};
